import React, { useEffect, useState } from 'react';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../../../firebase';
import { monthRange, shiftMonth, monthOf, monthLabel } from '../../../pages/EmployeePortal/LessonsTaught/lessonsTaughtModel';
import './MyLessonsTaught.css';

/**
 * A teacher's OWN monthly count of lessons she taught (Anthony, 2026-10-03:
 * "she should be able to see the amount of lessons that she's taught, but I do
 * not want her to see that there is an intended limit, or the pay structure").
 *
 * So this shows a count and the lessons behind it, and nothing else: no cap
 * (that lives in the admin-only schedulingConfig/teacherCaps and is never read
 * here), no rate, no other teacher. The query names her own teacherId, so the
 * page cannot list a colleague's lessons even by accident.
 *
 * "Taught" = attended lessons whose `taughtBy` is her, which the
 * stampLessonTaughtBy trigger records from the login that marked attendance.
 * Recording began October 2026, so earlier months say that rather than
 * showing a zero that would read as "you taught nothing."
 */

export const TRACKING_START = '2026-10';

const fmtDate = (s) => {
	const [y, m, d] = String(s || '').split('-').map(Number);
	if (!y) return s || '';
	return new Date(y, m - 1, d).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
};
const fmtTime = (hhmm) => {
	const m = /^(\d{1,2}):(\d{2})$/.exec(String(hhmm || ''));
	if (!m) return '';
	const h = Number(m[1]);
	return `${((h + 11) % 12) + 1}:${m[2]} ${h >= 12 ? 'PM' : 'AM'}`;
};

export default function MyLessonsTaught({ teacherId }) {
	const [month, setMonth] = useState(() => {
		const now = monthOf(new Date());
		return now < TRACKING_START ? TRACKING_START : now;
	});
	const [lessons, setLessons] = useState([]);
	const [status, setStatus] = useState('loading');
	const [open, setOpen] = useState(false);

	useEffect(() => {
		if (!teacherId) return undefined;
		let live = true;
		const range = monthRange(month);
		setStatus('loading');
		getDocs(query(
			collection(db, 'lessons'),
			where('taughtBy', '==', teacherId),
			where('date', '>=', range.from),
			where('date', '<=', range.to),
		))
			.then((snap) => {
				if (!live) return;
				const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }))
					.filter((r) => r.outcome === 'attended')
					.sort((a, b) => (a.date || '').localeCompare(b.date || '') || (a.time || '').localeCompare(b.time || ''));
				setLessons(rows);
				setStatus('ready');
			})
			.catch((e) => {
				console.error('My lessons taught: load failed', e);
				if (live) setStatus('error');
			});
		return () => { live = false; };
	}, [teacherId, month]);

	if (!teacherId) return null;

	const covered = lessons.filter((r) => r.teacherId && r.teacherId !== teacherId).length;
	const canGoBack = shiftMonth(month, -1) >= TRACKING_START;

	return (
		<section className='mlt' aria-label='Lessons you taught'>
			<div className='mlt__bar'>
				<button type='button' className='mlt__nav' onClick={() => setMonth((m) => shiftMonth(m, -1))}
					disabled={!canGoBack} aria-label='Previous month'>
					<i className='fa-sharp fa-solid fa-chevron-left' aria-hidden='true' />
				</button>
				<h2 className='mlt__month'>{monthLabel(month)}</h2>
				<button type='button' className='mlt__nav' onClick={() => setMonth((m) => shiftMonth(m, 1))} aria-label='Next month'>
					<i className='fa-sharp fa-solid fa-chevron-right' aria-hidden='true' />
				</button>
			</div>

			{status === 'loading' && <p className='mlt__line'>Loading your lessons…</p>}
			{status === 'error' && (
				<p className='mlt__line'>
					Your lessons could not load. Please tell Anthony right away.
					<span className='mlt__detail'>Lessons taught, {monthLabel(month)}</span>
				</p>
			)}
			{status === 'ready' && (
				<>
					<p className='mlt__line'>
						<span className='mlt__count'>{lessons.length}</span>
						{' '}lesson{lessons.length === 1 ? '' : 's'} taught
					</p>
					{covered > 0 && (
						<p className='mlt__detail'>
							Includes {covered} you covered for another teacher.
						</p>
					)}
					<p className='mlt__detail'>
						Counted from attendance you mark. A lesson you teach counts once you mark it Attended.
					</p>
					{lessons.length > 0 && (
						<button type='button' className='mlt__show' aria-expanded={open} onClick={() => setOpen((o) => !o)}>
							{open ? 'Hide lessons' : `Show ${lessons.length} lesson${lessons.length === 1 ? '' : 's'}`}
						</button>
					)}
					{open && (
						<ul className='mlt__list'>
							{lessons.map((r) => (
								<li key={r.id} className='mlt__item'>
									<span className='mlt__date'>{fmtDate(r.date)}{r.time ? `, ${fmtTime(r.time)}` : ''}</span>
									<span>{r.studentName || 'Unnamed'}</span>
								</li>
							))}
						</ul>
					)}
				</>
			)}
		</section>
	);
}
